
import React, { useState, useEffect } from 'react'
import { X, Upload, DollarSign, Building, Target } from 'lucide-react'
import { lumi } from '../lib/lumi'
import toast from 'react-hot-toast'

interface MovimentacaoBancariaFormProps {
  isOpen: boolean
  onClose: () => void
  onSuccess: () => void
  movimentacao?: any
}

export const MovimentacaoBancariaForm: React.FC<MovimentacaoBancariaFormProps> = ({
  isOpen,
  onClose,
  onSuccess,
  movimentacao
}) => {
  const [formData, setFormData] = useState({
    data: '',
    tipo: 'entrada',
    valor: '',
    conta_bancaria: '',
    conta_destino: '',
    categoria: 'outros',
    obra_id: '',
    centro_custo: '',
    descricao: '',
    documento: '',
    forma_pagamento: 'pix',
    status: 'pendente',
    observacoes: ''
  })

  const [contasBancarias, setContasBancarias] = useState<any[]>([])
  const [obras, setObras] = useState<any[]>([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (isOpen) {
      carregarDados()
      if (movimentacao) {
        setFormData({
          data: movimentacao.data?.split('T')[0] || '',
          tipo: movimentacao.tipo || 'entrada',
          valor: movimentacao.valor?.toString() || '',
          conta_bancaria: movimentacao.conta_bancaria || '',
          conta_destino: movimentacao.conta_destino || '',
          categoria: movimentacao.categoria || 'outros',
          obra_id: movimentacao.obra_id || '',
          centro_custo: movimentacao.centro_custo || '',
          descricao: movimentacao.descricao || '',
          documento: movimentacao.documento || '',
          forma_pagamento: movimentacao.forma_pagamento || 'pix',
          status: movimentacao.status || 'pendente',
          observacoes: movimentacao.observacoes || ''
        })
      } else {
        setFormData({
          data: new Date().toISOString().split('T')[0],
          tipo: 'entrada',
          valor: '',
          conta_bancaria: '',
          conta_destino: '',
          categoria: 'outros',
          obra_id: '',
          centro_custo: '',
          descricao: '',
          documento: '',
          forma_pagamento: 'pix',
          status: 'pendente',
          observacoes: ''
        })
      }
    }
  }, [isOpen, movimentacao])

  const carregarDados = async () => {
    try {
      const [contasResult, obrasResult] = await Promise.all([
        lumi.entities.contas_bancarias.list(),
        lumi.entities.planejamento_obras.list()
      ])
      
      setContasBancarias(contasResult.list || [])
      setObras(obrasResult.list || [])
    } catch (error) {
      console.error('Erro ao carregar dados:', error)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)

    try {
      const dados = {
        ...formData,
        valor: parseFloat(formData.valor),
        data: new Date(formData.data).toISOString(),
        conciliado: false,
        creator: 'admin',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      }

      if (movimentacao) {
        await lumi.entities.movimentacoes_bancarias.update(movimentacao._id, dados)
        toast.success('Movimentação atualizada com sucesso!')
      } else {
        await lumi.entities.movimentacoes_bancarias.create(dados)
        toast.success('Movimentação cadastrada com sucesso!')
      }

      onSuccess()
      onClose()
    } catch (error) {
      console.error('Erro ao salvar movimentação:', error)
      toast.error('Erro ao salvar movimentação')
    } finally {
      setLoading(false)
    }
  }

  const handleChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))

    // Auto-gerar centro de custo baseado na obra e categoria
    if (field === 'obra_id' || field === 'categoria') {
      const obra = field === 'obra_id' ? value : formData.obra_id
      const categoria = field === 'categoria' ? value : formData.categoria
      
      if (obra && categoria) {
        const centroCusto = `${categoria}_${obra}`
        setFormData(prev => ({
          ...prev,
          [field]: value,
          centro_custo: centroCusto
        }))
      } else {
        setFormData(prev => ({
          ...prev,
          [field]: value
        }))
      }
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-gray-900">
              {movimentacao ? 'Editar' : 'Nova'} Movimentação Bancária
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <X size={24} />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Data */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Data *
              </label>
              <input
                type="date"
                value={formData.data}
                onChange={(e) => handleChange('data', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>

            {/* Tipo */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tipo *
              </label>
              <select
                value={formData.tipo}
                onChange={(e) => handleChange('tipo', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              >
                <option value="entrada">Entrada</option>
                <option value="saida">Saída</option>
                <option value="transferencia">Transferência</option>
              </select>
            </div>

            {/* Valor */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Valor *
              </label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
                <input
                  type="number"
                  step="0.01"
                  value={formData.valor}
                  onChange={(e) => handleChange('valor', e.target.value)}
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="0,00"
                  required
                />
              </div>
            </div>

            {/* Conta Bancária */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Conta Bancária *
              </label>
              <select
                value={formData.conta_bancaria}
                onChange={(e) => handleChange('conta_bancaria', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              >
                <option value="">Selecione a conta</option>
                {contasBancarias.map((conta) => (
                  <option key={conta._id} value={`${conta.banco} - ${conta.tipo_conta.toUpperCase()} ${conta.conta}-${conta.digito}`}>
                    {conta.banco} - {conta.tipo_conta.toUpperCase()} {conta.conta}-{conta.digito}
                  </option>
                ))}
              </select>
            </div>

            {/* Conta Destino (apenas para transferências) */}
            {formData.tipo === 'transferencia' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Conta Destino *
                </label>
                <select
                  value={formData.conta_destino}
                  onChange={(e) => handleChange('conta_destino', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required={formData.tipo === 'transferencia'}
                >
                  <option value="">Selecione a conta destino</option>
                  {contasBancarias.map((conta) => (
                    <option key={conta._id} value={`${conta.banco} - ${conta.tipo_conta.toUpperCase()} ${conta.conta}-${conta.digito}`}>
                      {conta.banco} - {conta.tipo_conta.toUpperCase()} {conta.conta}-{conta.digito}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Categoria */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Categoria *
              </label>
              <select
                value={formData.categoria}
                onChange={(e) => handleChange('categoria', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              >
                <option value="recebimento">Recebimento</option>
                <option value="mao_obra">Mão de Obra</option>
                <option value="material">Material</option>
                <option value="administrativo">Administrativo</option>
                <option value="tributario">Tributário</option>
                <option value="equipamentos">Equipamentos</option>
                <option value="financeiro">Financeiro</option>
                <option value="outros">Outros</option>
              </select>
            </div>

            {/* Obra */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Obra
              </label>
              <div className="relative">
                <Building className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
                <select
                  value={formData.obra_id}
                  onChange={(e) => handleChange('obra_id', e.target.value)}
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Selecione a obra</option>
                  {obras.map((obra) => (
                    <option key={obra._id} value={obra._id}>
                      {obra.nome_projeto}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Centro de Custo */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Centro de Custo
              </label>
              <div className="relative">
                <Target className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
                <input
                  type="text"
                  value={formData.centro_custo}
                  onChange={(e) => handleChange('centro_custo', e.target.value)}
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Ex: materiais_obra_001"
                />
              </div>
            </div>

            {/* Forma de Pagamento */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Forma de Pagamento *
              </label>
              <select
                value={formData.forma_pagamento}
                onChange={(e) => handleChange('forma_pagamento', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              >
                <option value="pix">PIX</option>
                <option value="transferencia">Transferência</option>
                <option value="ted">TED</option>
                <option value="doc">DOC</option>
                <option value="boleto">Boleto</option>
                <option value="cartao">Cartão</option>
                <option value="cheque">Cheque</option>
                <option value="dinheiro">Dinheiro</option>
              </select>
            </div>

            {/* Status */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Status *
              </label>
              <select
                value={formData.status}
                onChange={(e) => handleChange('status', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              >
                <option value="pendente">Pendente</option>
                <option value="processando">Processando</option>
                <option value="concluido">Concluído</option>
                <option value="cancelado">Cancelado</option>
              </select>
            </div>

            {/* Documento */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Nº Documento/Comprovante
              </label>
              <input
                type="text"
                value={formData.documento}
                onChange={(e) => handleChange('documento', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Ex: TED123456, PIX789012"
              />
            </div>
          </div>

          {/* Descrição */}
          <div className="mt-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Descrição *
            </label>
            <input
              type="text"
              value={formData.descricao}
              onChange={(e) => handleChange('descricao', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Descreva a movimentação"
              required
            />
          </div>

          {/* Observações */}
          <div className="mt-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Observações
            </label>
            <textarea
              value={formData.observacoes}
              onChange={(e) => handleChange('observacoes', e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Observações adicionais..."
            />
          </div>

          {/* Botões */}
          <div className="flex justify-end gap-3 mt-8">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {loading ? 'Salvando...' : movimentacao ? 'Atualizar' : 'Cadastrar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default MovimentacaoBancariaForm
